import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle, faExclamationTriangle, faQuestionCircle, faTimesCircle,
  faChevronDown, faChevronRight, faExchangeAlt, faUndo, faSearch, faGlobe
} from '@fortawesome/free-solid-svg-icons';

const enforcementBadge = (enforcement, isDarkMode) => {
  const configs = {
    enforced: { icon: faCheckCircle, color: 'text-green-400', bg: isDarkMode ? 'bg-green-900/20' : 'bg-green-50', label: 'Enforced' },
    bypassed: { icon: faExclamationTriangle, color: 'text-red-400', bg: isDarkMode ? 'bg-red-900/20' : 'bg-red-50', label: 'Bypassed' },
    partial: { icon: faQuestionCircle, color: 'text-yellow-400', bg: isDarkMode ? 'bg-yellow-900/20' : 'bg-yellow-50', label: 'Review' },
    error: { icon: faTimesCircle, color: 'text-gray-400', bg: isDarkMode ? 'bg-gray-800' : 'bg-gray-100', label: 'Error' }
  };
  const c = configs[enforcement] || configs.error;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${c.bg} ${c.color}`}>
      <FontAwesomeIcon icon={c.icon} />
      {c.label}
    </span>
  );
};

const STORAGE_KEY = 'authsmash-table-filters';
const DEFAULT_FILTERS = {
  filter: 'all',
  urlText: '',
  domain: 'all'
};

const AuthSmashResultsTable = ({ endpoints = [], isDarkMode }) => {
  const [expandedRow, setExpandedRow] = useState(null);

  // Load filters from localStorage or use defaults
  const [filters, setFilters] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? { ...DEFAULT_FILTERS, ...JSON.parse(saved) } : DEFAULT_FILTERS;
    } catch {
      return DEFAULT_FILTERS;
    }
  });

  const { filter, urlText, domain } = filters;

  // Save filters to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  }, [filters]);

  const updateFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const resetFilters = () => {
    setFilters(DEFAULT_FILTERS);
  };

  // Extract unique domains from endpoints
  const uniqueDomains = useMemo(() => {
    if (!endpoints || !Array.isArray(endpoints)) return [];
    const domains = new Set();
    endpoints.forEach(ep => {
      if (ep?.url) {
        const match = ep.url.match(/^https?:\/\/([^/]+)/);
        if (match) domains.add(match[1]);
      }
    });
    return Array.from(domains).sort();
  }, [endpoints]);

  const filtered = useMemo(() => {
    if (!endpoints || !Array.isArray(endpoints)) return [];

    return endpoints.filter(ep => {
      if (!ep) return false;

      // Apply URL text filter
      if (urlText && ep.url && !ep.url.toLowerCase().includes(urlText.toLowerCase())) {
        return false;
      }

      // Apply domain filter
      if (domain !== 'all') {
        const epDomain = ep.url?.match(/^https?:\/\/([^/]+)/)?.[1];
        if (epDomain !== domain) return false;
      }

      // Apply severity filter
      if (filter === 'all') return true;

      const allEnforcements = [
        ...(ep.profiles || []).map(p => p?.enforcement).filter(Boolean),
        ...(ep.unauthenticated ? [ep.unauthenticated.enforcement] : [])
      ];
      return allEnforcements.includes(filter);
    });
  }, [endpoints, urlText, domain, filter]);

  const headerCl = `text-[10px] font-medium px-3 py-2 text-left ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`;
  const cellCl = `px-3 py-2 text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`;
  const stickyShadow = 'shadow-[2px_0_4px_rgba(0,0,0,0.1)]';

  const methodColor = (method) => {
    const colors = { GET: 'text-green-400', POST: 'text-blue-400', PUT: 'text-amber-400', DELETE: 'text-red-400', PATCH: 'text-purple-400' };
    return colors[method] || 'text-gray-400';
  };

  // Get content type badge info from endpoint
  const getContentTypeBadge = (url) => {
    const urlLower = url.toLowerCase();
    if (urlLower.includes('.json') || urlLower.includes('/api/') || urlLower.includes('/v1/') || urlLower.includes('/graphql')) {
      return { label: 'JSON', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' };
    } else if (urlLower.includes('.xml')) {
      return { label: 'XML', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' };
    } else if (urlLower.includes('.css')) {
      return { label: 'CSS', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' };
    } else if (urlLower.includes('.js')) {
      return { label: 'JS', color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300' };
    } else if (urlLower.match(/\.(jpg|jpeg|png|gif|svg|webp|ico)$/)) {
      return { label: 'IMG', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' };
    } else if (urlLower.includes('.pdf')) {
      return { label: 'PDF', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' };
    } else {
      return { label: 'HTML', color: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300' };
    }
  };

  // Get row background based on worst enforcement
  const rowBg = (ep) => {
    const allEnforcements = [
      ...ep.profiles.map(p => p.enforcement),
      ...(ep.unauthenticated ? [ep.unauthenticated.enforcement] : [])
    ];
    if (allEnforcements.includes('bypassed')) return isDarkMode ? 'bg-red-900/10' : 'bg-red-50/50';
    if (allEnforcements.includes('partial')) return isDarkMode ? 'bg-yellow-900/10' : 'bg-yellow-50/50';
    return '';
  };

  // Helper to smartly truncate URL
  const smartTruncateUrl = (url, maxLength = 50) => {
    const pathOnly = url.replace(/^https?:\/\/[^/]+/, '');
    if (pathOnly.length <= maxLength) return pathOnly;

    // Show start and end, collapse middle
    const start = pathOnly.substring(0, Math.floor(maxLength * 0.6));
    const end = pathOnly.substring(pathOnly.length - Math.floor(maxLength * 0.25));
    return `${start}...${end}`;
  };

  return (
    <div className="h-full flex flex-col">
      {/* Filter bar */}
      <div className={`flex items-center gap-2 px-3 py-1.5 border-b flex-wrap ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
        <span className={`text-[10px] font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Status:</span>
        {['all', 'bypassed', 'partial', 'enforced'].map(f => (
          <button
            key={f}
            onClick={() => updateFilter('filter', f)}
            className={`px-2 py-0.5 rounded text-[10px] font-medium ${
              filter === f
                ? (isDarkMode ? 'bg-gray-700 text-white' : 'bg-gray-200 text-gray-800')
                : (isDarkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600')
            }`}
          >
            {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}

        {/* URL text filter */}
        <div className="flex items-center gap-1 ml-2">
          <FontAwesomeIcon icon={faSearch} className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
          <input
            type="text"
            placeholder="Filter URL..."
            value={urlText}
            onChange={e => updateFilter('urlText', e.target.value)}
            className={`px-2 py-0.5 rounded text-xs w-32 ${
              isDarkMode
                ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-500'
                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
            } border focus:outline-none focus:ring-1 focus:ring-blue-500`}
          />
        </div>

        {/* Domain filter */}
        {uniqueDomains.length > 1 && (
          <div className="flex items-center gap-1">
            <FontAwesomeIcon icon={faGlobe} className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
            <select
              value={domain}
              onChange={e => updateFilter('domain', e.target.value)}
              className={`px-2 py-0.5 rounded text-xs ${
                isDarkMode
                  ? 'bg-gray-700 border-gray-600 text-gray-200'
                  : 'bg-white border-gray-300 text-gray-900'
              } border focus:outline-none focus:ring-1 focus:ring-blue-500`}
            >
              <option value="all">All domains</option>
              {uniqueDomains.map(d => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>
        )}

        <button
          onClick={resetFilters}
          className={`px-2 py-1 rounded text-xs flex items-center gap-1 transition-colors ${
            isDarkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-600 hover:text-gray-800 hover:bg-gray-200'
          }`}
          title="Reset filters to default"
        >
          <FontAwesomeIcon icon={faUndo} className="text-[10px]" />
          Reset
        </button>

        <span className={`text-xs ml-auto ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          Showing {filtered.length} of {endpoints.length} endpoints
        </span>
      </div>

      {/* Table with sticky columns */}
      <div className="flex-1 overflow-auto">
        <table className="w-full table-fixed">
          <thead className={`sticky top-0 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'} z-10`}>
            <tr className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <th className={`${headerCl} w-8 sticky left-0 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}></th>
              <th className={`${headerCl} w-20 sticky left-8 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>Method</th>
              <th className={`${headerCl} w-80 sticky left-28 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'} ${stickyShadow}`}>URL</th>
              <th className={`${headerCl} w-28 text-center`}>Baseline</th>
              {/* Dynamic profile columns - gather unique profile names */}
              {(() => {
                const profileNames = new Set();
                endpoints.forEach(ep => ep.profiles.forEach(p => profileNames.add(p.profileName)));
                return Array.from(profileNames).map(name => (
                  <th key={name} className={`${headerCl} w-32 text-center`}>{name}</th>
                ));
              })()}
              {endpoints.some(ep => ep.unauthenticated) && (
                <th className={`${headerCl} w-32 text-center`}>Unauth</th>
              )}
            </tr>
          </thead>
          <tbody>
            {filtered.map((ep, idx) => {
              const isExpanded = expandedRow === idx;
              const profileNames = new Set();
              endpoints.forEach(e => e.profiles.forEach(p => profileNames.add(p.profileName)));
              const profileNameArr = Array.from(profileNames);

              return (
                <React.Fragment key={idx}>
                  <tr
                    onClick={() => setExpandedRow(isExpanded ? null : idx)}
                    className={`cursor-pointer border-b transition-colors ${rowBg(ep)} ${isDarkMode ? 'border-gray-800 hover:bg-gray-800/50' : 'border-gray-100 hover:bg-gray-50'}`}
                  >
                    <td className={`${cellCl} sticky left-0 ${rowBg(ep) || (isDarkMode ? 'bg-gray-900' : 'bg-white')}`}>
                      <FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronRight} className={`text-[10px] ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`} />
                    </td>
                    <td className={`${cellCl} sticky left-8 ${rowBg(ep) || (isDarkMode ? 'bg-gray-900' : 'bg-white')}`}>
                      <span className={`font-mono font-medium ${methodColor(ep.method)}`}>{ep.method}</span>
                    </td>
                    <td className={`${cellCl} sticky left-28 ${rowBg(ep) || (isDarkMode ? 'bg-gray-900' : 'bg-white')} ${stickyShadow}`}>
                      <div className="flex items-center gap-1.5 overflow-hidden">
                        <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-medium flex-shrink-0 ${getContentTypeBadge(ep.url).color}`}>
                          {getContentTypeBadge(ep.url).label}
                        </span>
                        <span className="font-mono text-[11px] truncate flex-1 min-w-0" title={ep.url}>
                          {smartTruncateUrl(ep.url)}
                        </span>
                      </div>
                    </td>
                    <td className={`${cellCl} text-center`}>
                      <span className={`font-mono ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {ep.baseline?.statusCode || '—'}
                      </span>
                      <span className={`block text-[9px] ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
                        {ep.baseline?.contentLength != null ? `${ep.baseline.contentLength}b` : ''}
                      </span>
                    </td>
                    {profileNameArr.map(name => {
                      const profile = ep.profiles.find(p => p.profileName === name);
                      return (
                        <td key={name} className={`${cellCl} text-center`}>
                          {profile ? (
                            <>
                              {enforcementBadge(profile.enforcement, isDarkMode)}
                              <span className={`block text-[9px] mt-0.5 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
                                {profile.statusCode} / {profile.contentLength != null ? `${profile.contentLength}b` : '—'}
                              </span>
                            </>
                          ) : (
                            <span className={isDarkMode ? 'text-gray-600' : 'text-gray-400'}>—</span>
                          )}
                        </td>
                      );
                    })}
                    {endpoints.some(e => e.unauthenticated) && (
                      <td className={`${cellCl} text-center`}>
                        {ep.unauthenticated ? (
                          <>
                            {enforcementBadge(ep.unauthenticated.enforcement, isDarkMode)}
                            <span className={`block text-[9px] mt-0.5 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
                              {ep.unauthenticated.statusCode} / {ep.unauthenticated.contentLength != null ? `${ep.unauthenticated.contentLength}b` : '—'}
                            </span>
                          </>
                        ) : (
                          <span className={isDarkMode ? 'text-gray-600' : 'text-gray-400'}>—</span>
                        )}
                      </td>
                    )}
                  </tr>

                  {/* Expanded detail row */}
                  {isExpanded && (
                    <tr className={isDarkMode ? 'bg-gray-850' : 'bg-gray-50'}>
                      <td colSpan={4 + profileNameArr.length + (endpoints.some(e => e.unauthenticated) ? 1 : 0)}>
                        <div className={`py-3 space-y-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          {/* Padding left to account for sticky columns (32 + 80 + 320 = 432px) */}
                          <div className="pl-[440px] pr-6">
                            <div className="text-[10px] font-medium mb-1">Comparison Details</div>
                            <div className="font-mono text-[11px] break-all mb-2">{ep.url}</div>

                          {ep.profiles.map((profile, pi) => (
                            <div key={pi} className={`flex items-start gap-3 p-2 rounded text-xs ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                              <div className="w-24 shrink-0">
                                <div className="font-medium">{profile.profileName}</div>
                                {enforcementBadge(profile.enforcement, isDarkMode)}
                              </div>
                              <div className="flex-1">
                                <div className="flex gap-4 text-[10px]">
                                  <span>Status: {ep.baseline?.statusCode} → {profile.statusCode}
                                    {profile.comparison?.statusDiff && <FontAwesomeIcon icon={faExchangeAlt} className="ml-1 text-amber-400" />}
                                  </span>
                                  <span>Body: {ep.baseline?.contentLength}b → {profile.contentLength}b
                                    ({profile.comparison?.bodyLengthPct || 0}% diff)
                                  </span>
                                  {profile.comparison?.exactBodyMatch && (
                                    <span className="text-red-400">Exact body match</span>
                                  )}
                                  {profile.comparison?.enforceKeywordFound && (
                                    <span className="text-green-400">Enforce keyword found</span>
                                  )}
                                </div>
                                <div className={`mt-1 text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                  {profile.comparison?.reason}
                                </div>
                              </div>
                            </div>
                          ))}

                          {ep.unauthenticated && (
                            <div className={`flex items-start gap-3 p-2 rounded text-xs ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                              <div className="w-24 shrink-0">
                                <div className="font-medium">Unauth</div>
                                {enforcementBadge(ep.unauthenticated.enforcement, isDarkMode)}
                              </div>
                              <div className="flex-1">
                                <div className="flex gap-4 text-[10px]">
                                  <span>Status: {ep.baseline?.statusCode} → {ep.unauthenticated.statusCode}</span>
                                  <span>Body: {ep.baseline?.contentLength}b → {ep.unauthenticated.contentLength}b
                                    ({ep.unauthenticated.comparison?.bodyLengthPct || 0}% diff)
                                  </span>
                                  {ep.unauthenticated.comparison?.exactBodyMatch && (
                                    <span className="text-red-400">Exact body match</span>
                                  )}
                                </div>
                                <div className={`mt-1 text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                  {ep.unauthenticated.comparison?.reason}
                                </div>
                              </div>
                            </div>
                          )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>

        {filtered.length === 0 && (
          <div className={`text-center py-8 text-sm ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
            {filter !== 'all' ? 'No endpoints match the selected filter.' : 'No results yet.'}
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthSmashResultsTable;
